import { useCurrency } from "@/context/CurrencyContext";
import { CurrencyDollar, CurrencyBtc, ArrowsClockwise, LinkSimple } from "@phosphor-icons/react";

function formatRelative(iso) {
  if (!iso) return "sin datos";
  const t = new Date(iso).getTime();
  if (!t) return "sin datos";
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return "recién";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

/**
 * Widget de cotización + selector de moneda.
 * Se usa en el Dashboard (ya no en la Topbar global).
 */
export function CotizacionWidget() {
  const {
    currency, setCurrency,
    cotizacion, setCotizacion,
    cotizMeta, cotizLoading, cotizManual,
    refreshCotizacion,
  } = useCurrency();

  const handleRefresh = async () => {
    await refreshCotizacion({ force: true, overrideManual: true });
  };

  const tooltipParts = [];
  if (cotizMeta) {
    tooltipParts.push(`Oficial venta $${cotizMeta.venta?.toFixed(2) ?? "—"}`);
    if (cotizMeta.compra) tooltipParts.push(`compra $${cotizMeta.compra.toFixed(2)}`);
    tooltipParts.push(`fuente: ${cotizMeta.source || "dolarhoy.com"}`);
    tooltipParts.push(`actualizado ${formatRelative(cotizMeta.updated_at)}`);
    if (cotizMeta.stale) tooltipParts.push("⚠ dato viejo (fallback del cache)");
  }
  const tooltip = tooltipParts.join(" · ");

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-1.5" title={tooltip} data-testid="cotizacion-widget">
        <label className="text-[11px] uppercase tracking-widest text-[#666] font-mono">USD/ARS</label>
        <NumberInput
          data-testid="cotizacion-input"
          className="w-24 h-8 rounded-sm font-mono text-right text-[13px]"
          value={cotizacion}
          onChange={(e) => setCotizacion(parseFloat(e.target.value) || 0)}
        />
        <button
          type="button"
          data-testid="cotizacion-refresh"
          onClick={handleRefresh}
          disabled={cotizLoading}
          title={cotizLoading ? "Actualizando…" : "Sincronizar con dolarhoy.com"}
          className={`h-8 w-8 rounded-sm border border-[#E5E5E5] flex items-center justify-center transition ${
            cotizLoading ? "opacity-60 cursor-wait" : "hover:bg-neutral-50 hover:border-[#6B1F24]/40"
          }`}
        >
          <ArrowsClockwise size={13} className={cotizLoading ? "animate-spin" : ""} />
        </button>
        <div className="flex flex-col leading-tight ml-1">
          <a
            href={cotizMeta?.source_url || "https://dolarhoy.com/cotizaciondolaroficial"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-mono uppercase tracking-wider text-[#079a7d] hover:underline flex items-center gap-1"
            data-testid="cotizacion-source-link"
            title="Abrir dolarhoy.com — Dólar Oficial"
          >
            <LinkSimple size={9} weight="bold" />
            {cotizMeta?.source ? "dolarhoy" : "manual"}
          </a>
          <span className="text-[9px] font-mono text-[#999]" data-testid="cotizacion-timestamp">
            {cotizManual
              ? "editado a mano"
              : cotizMeta
                ? formatRelative(cotizMeta.updated_at) + (cotizMeta.stale ? " ⚠" : "")
                : "sin datos"}
          </span>
        </div>
      </div>
      <div
        data-testid="currency-toggle"
        className="inline-flex rounded-sm border border-[#E5E5E5] overflow-hidden"
      >
        <button
          data-testid="currency-ars"
          onClick={() => setCurrency("ARS")}
          className={`px-3 py-1.5 text-[12px] font-mono flex items-center gap-1 transition-colors duration-150 ${
            currency === "ARS" ? "bg-[#6B1F24] text-white" : "bg-white text-[#666] hover:bg-neutral-50"
          }`}
        >
          <CurrencyBtc size={14} /> ARS
        </button>
        <button
          data-testid="currency-usd"
          onClick={() => setCurrency("USD")}
          className={`px-3 py-1.5 text-[12px] font-mono flex items-center gap-1 transition-colors duration-150 ${
            currency === "USD" ? "bg-[#6B1F24] text-white" : "bg-white text-[#666] hover:bg-neutral-50"
          }`}
        >
          <CurrencyDollar size={14} /> USD
        </button>
      </div>
    </div>
  );
}

import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isRouteReadOnly } from "@/lib/permissions";
import NumberInput from "@/components/ui/NumberInput";

export default function Topbar({ title, subtitle, right, left }) {
  const { user } = useAuth();
  const location = useLocation();
  const readOnly = user && !user.is_admin && isRouteReadOnly(user, location.pathname);
  return (
    <>
      <header className="border-b border-[#E5E5E5] bg-white px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <h1 className="font-heading text-[20px] font-semibold text-[#1A1A1A] leading-tight shrink-0">
            {title}
          </h1>
          {left}
        </div>

        <div className="flex items-center gap-4">
          {right}
        </div>
      </header>
      {readOnly && (
        <div
          className="bg-amber-100 border-b border-amber-400 text-amber-900 px-6 py-2 flex items-center gap-2 text-[12px] font-mono"
          data-testid="readonly-banner"
        >
          <span className="text-lg leading-none">🔒</span>
          <span className="font-bold uppercase tracking-wider text-[11px]">Modo solo lectura</span>
          <span className="opacity-90">
            Podés ver este módulo pero no cargar ni modificar registros. Contactá al administrador para habilitar escritura.
          </span>
        </div>
      )}
    </>
  );
}
